import i18n from '@/shared/i18n/config';
import { API_ENDPOINTS, apiClient } from '@/shared/apiClient';
import type { ReportNode } from '../types/report';
import { readReportBody, type ReportBody } from '../utils/reportBody';
import type { PrintLayoutSettings } from '@/features/editor/printLayout';

export async function persistReportPrintSettings(
  projectId: string, reportId: string, patch: Partial<PrintLayoutSettings>, requireOwner: () => void,
): Promise<Partial<PrintLayoutSettings>> {
  requireOwner();
  const fields = { paperSize: 'paper_size', footerText: 'footer_text', pageNumberPosition: 'page_number_position' } as const;
  const payload = Object.fromEntries(Object.entries(patch).map(([key, value]) => [fields[key as keyof typeof fields], value]));
  const response = await apiClient.patch<{ data: { report: ReportNode } }>(
    API_ENDPOINTS.reports.update(projectId, reportId), { print_layout_settings: payload }, 'reports',
  );
  requireOwner();
  const saved = response?.data?.report;
  const layout = saved?.customFields?.printLayoutSettings as Partial<PrintLayoutSettings> | undefined;
  if (!saved || String(saved.id) !== reportId || (saved.projectId && saved.projectId !== projectId) || !layout
    || !Object.entries(patch).every(([key, value]) => layout[key as keyof PrintLayoutSettings] === value)) {
    throw new Error(i18n.t('The server did not confirm this report draft.'));
  }
  return layout;
}

export async function persistReportDocumentBody(
  projectId: string, reportId: string, body: ReportBody, expected: ReportBody, requireOwner: () => void,
): Promise<ReportNode> {
  requireOwner();
  const response = await apiClient.patch<{ data: { report: ReportNode } }>(
    API_ENDPOINTS.reports.update(projectId, reportId),
    { content: body.content, report_block_document: body.blocks, expected_body: { content: expected.content, report_block_document: expected.blocks } },
    'reports',
  ).catch((error: unknown) => {
    if (error && typeof error === 'object' && 'status' in error && error.status === 409) {
      throw new Error(i18n.t('This report changed. Compare the saved version before retrying your draft.'));
    }
    throw error;
  });
  requireOwner();
  const report = response?.data?.report;
  if (!report || String(report.id) !== reportId || JSON.stringify(readReportBody(report)) !== JSON.stringify(body)) {
    throw new Error(i18n.t('The server did not confirm this report draft.'));
  }
  return report;
}
